/**
 * CdPenaltyRulesPage — Reference Data · CD Penalty Rules.
 *
 * CD early-withdrawal penalty schedule. Each row maps a term range (in
 * months) to a penalty (months of interest forfeited). Read by every CD
 * account-opening + maintenance flow.
 *
 * Contract derived from primary sources (2026-05-18):
 *   - Entity:  PCBJxChangeApi/Data/Entities/Config/Codes/CdPenaltyRule.cs
 *   - Sibling: CdPenaltyWaiverReason.cs (also returned by the read endpoint
 *              but managed elsewhere — not edited here)
 *   - Read:    ConfigFunction.cs:477-491 returns
 *                { success:true, data:{ rules, waiverReasons } }
 *              No IsActive filter — admin sees inactive rows.
 *   - Write:   AdminSystemFunction.cs POST /v3/admin/cd-penalty-rules
 *   - Delete:  AdminSystemFunction.cs DELETE /v3/admin/cd-penalty-rules/{ruleCode}
 *   - DTO:     CdPenaltyRuleUpsertInput (ruleCode/displayName/termMin/Max
 *              Months/penaltyMonths/sortOrder/isActive)
 *
 * NOT a port of any sibling editor. The api-client `cdPenaltyRules()` is
 * mis-typed as `Promise<ApiResult<ReadonlyArray<CdPenaltyRule>>>` but the
 * wire actually returns an OBJECT (`{ rules, waiverReasons }`) wrapped by
 * the `Ok()` helper — same trap as `slaTargets()`. Page casts the
 * response through `unknown` to the correct shape.
 *
 * Auth: MSAL Path B via ADMIN_AUTH_TOKEN_SENTINEL.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type CdPenaltyRuleUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, NumberField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface CdPenaltyRuleRow {
  ruleCode: string;
  displayName: string;
  termMinMonths: number;
  termMaxMonths: number;
  penaltyMonths: number;
  isActive: boolean;
  sortOrder?: number | null;
}

interface CdPenaltyRulesPayload {
  rules: ReadonlyArray<CdPenaltyRuleRow>;
  // waiverReasons also lives in this response; surfaced in a future
  // CdPenaltyWaiverReasonsPage. v0.9.8 only edits `rules`.
}

interface EditDraft {
  isNew: boolean;
  ruleCode: string;
  displayName: string;
  termMinMonths: number;
  termMaxMonths: number;
  penaltyMonths: number;
  sortOrder: number;
  isActive: boolean;
  lockedKey: boolean;
}

function emptyDraft(): EditDraft {
  return {
    isNew: true,
    ruleCode: '',
    displayName: '',
    termMinMonths: 0,
    termMaxMonths: 0,
    penaltyMonths: 0,
    sortOrder: 0,
    isActive: true,
    lockedKey: false,
  };
}

function rowToDraft(row: CdPenaltyRuleRow): EditDraft {
  return {
    isNew: false,
    ruleCode: row.ruleCode,
    displayName: row.displayName,
    termMinMonths: row.termMinMonths,
    termMaxMonths: row.termMaxMonths,
    penaltyMonths: row.penaltyMonths,
    sortOrder: row.sortOrder ?? 0,
    isActive: row.isActive,
    lockedKey: true,
  };
}

function draftToUpsertBody(d: EditDraft): CdPenaltyRuleUpsertBody {
  return {
    ruleCode: d.ruleCode.trim(),
    displayName: d.displayName.trim(),
    termMinMonths: d.termMinMonths,
    termMaxMonths: d.termMaxMonths,
    penaltyMonths: d.penaltyMonths,
    sortOrder: d.sortOrder,
    isActive: d.isActive,
  };
}

function termRangeLabel(min: number, max: number): string {
  if (min === max) return `${min} mo`;
  return `${min}–${max} mo`;
}

export function CdPenaltyRulesPage(): JSX.Element {
  const client = useApiClient();
  const [rules, setRules] = useState<ReadonlyArray<CdPenaltyRuleRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRules(null);
      setLoadError(null);
      try {
        // api-client mis-types cdPenaltyRules() as Promise<ApiResult<
        // ReadonlyArray<CdPenaltyRule>>> but the wire actually returns
        // { success, data: { rules, waiverReasons } }. Cast through
        // unknown to the correct shape.
        const raw = (await client.retail.config.cdPenaltyRules()) as unknown as {
          success: boolean;
          error?: string;
          data?: CdPenaltyRulesPayload;
        };
        if (cancelled) return;
        if (raw.success && raw.data) {
          setRules(raw.data.rules);
        } else {
          setLoadError(raw.error ?? 'Failed to load CD penalty rules.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const filtered = useMemo(() => {
    if (!rules) return rules;
    const n = filter.trim().toLowerCase();
    if (!n) return rules;
    return rules.filter((r) => {
      const hay = `${r.ruleCode} ${r.displayName}`.toLowerCase();
      return hay.includes(n);
    });
  }, [rules, filter]);

  const columns: ReadonlyArray<DataTableColumn<CdPenaltyRuleRow>> = useMemo(
    () => [
      {
        header: 'Rule code',
        cell: (r) => <code className="cell-mono--bold">{r.ruleCode}</code>,
      },
      {
        header: 'Display name',
        cell: (r) => r.displayName,
      },
      {
        header: 'Term range',
        cell: (r) => (
          <span className="cell-mono">{termRangeLabel(r.termMinMonths, r.termMaxMonths)}</span>
        ),
      },
      {
        header: 'Penalty',
        cell: (r) => <span className="cell-mono">{r.penaltyMonths} mo interest</span>,
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => <Button onClick={() => setDraft(rowToDraft(r))}>Edit</Button>,
      },
    ],
    [],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.ruleCode.trim()) {
      setSaveError('Rule code is required.');
      return;
    }
    if (!draft.displayName.trim()) {
      setSaveError('Display name is required.');
      return;
    }
    if (draft.termMinMonths < 0 || draft.termMaxMonths < 0) {
      setSaveError('Term months cannot be negative.');
      return;
    }
    if (draft.termMaxMonths < draft.termMinMonths) {
      setSaveError('Term max must be ≥ term min.');
      return;
    }
    if (draft.penaltyMonths < 0) {
      setSaveError('Penalty months cannot be negative.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.cdPenaltyRules.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created CD penalty rule "${draft.ruleCode}".`
          : `Updated CD penalty rule "${draft.ruleCode}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDelete = useCallback(async () => {
    if (!draft || draft.isNew) return;
    const code = draft.ruleCode.trim();
    if (!code) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.cdPenaltyRules.remove(ADMIN_AUTH_TOKEN_SENTINEL, code);
      if (!resp.success) {
        setSaveError(resp.error ?? 'delete returned success=false');
        return;
      }
      setDraft(null);
      setToast({ tone: 'warn', text: `Deleted CD penalty rule "${code}".` });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load CD penalty rules: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rules) return <Spinner label="Loading CD penalty rules…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`CD Penalty Rules — ${filtered?.length ?? 0} of ${rules.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="Filter by code or name…"
              />
            </div>
            <Button variant="primary" onClick={() => setDraft(emptyDraft())}>
              + Add Rule
            </Button>
          </div>
        }
      >
        <DataTable<CdPenaltyRuleRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.ruleCode}
          emptyState={<p className="empty">No CD penalty rules match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add CD Penalty Rule' : `Edit ${draft?.ruleCode ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={560}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              {!draft.isNew && (
                <Button onClick={() => void handleDelete()} disabled={saving}>
                  Delete
                </Button>
              )}
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Rule code"
              value={draft.ruleCode}
              onChange={(v) => setDraft({ ...draft, ruleCode: v })}
              placeholder="e.g. PENALTY_12_MO"
              disabled={draft.lockedKey}
              hint={
                draft.lockedKey
                  ? 'Rule code is permanent — delete and re-create to re-key.'
                  : 'Stable identifier; permanent once set. Use a clear naming convention (e.g. PENALTY_<TermMin>_<TermMax>).'
              }
            />

            <TextField
              label="Display name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v })}
              placeholder="Short label surfaced in the CD account-opening flow."
            />

            <NumberField
              label="Term min (months)"
              value={draft.termMinMonths}
              onChange={(v) => setDraft({ ...draft, termMinMonths: v ?? 0 })}
              min={0}
              hint="Inclusive lower bound. Rule applies when the CD term is ≥ this value."
            />

            <NumberField
              label="Term max (months)"
              value={draft.termMaxMonths}
              onChange={(v) => setDraft({ ...draft, termMaxMonths: v ?? 0 })}
              min={0}
              hint="Inclusive upper bound. Rule applies when the CD term is ≤ this value."
            />

            <NumberField
              label="Penalty (months of interest)"
              value={draft.penaltyMonths}
              onChange={(v) => setDraft({ ...draft, penaltyMonths: v ?? 0 })}
              min={0}
              hint="Months of interest forfeited on early withdrawal."
            />

            <NumberField
              label="Sort order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v ?? 0 })}
              min={0}
              hint="Display order in account-opening dropdowns. Lower numbers first."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="When inactive, the rule is hidden from account-opening flows but the row stays for audit + historical CDs."
            />
          </div>
        )}
      </ModalShell>
    </>
  );
}
